import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { IntegrationSummary } from '@wrapforce/shared';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { apiErrorMessage } from '@/lib/api-errors';
import { useConnectIntegration, useDisconnectIntegration } from './api';
import { ProviderLogo } from './ProviderLogo';

const STATUS_LABEL_KEYS: Record<IntegrationSummary['status'], string> = {
  disconnected: 'status.disconnected',
  pending_setup: 'status.pendingSetup',
  connected: 'status.connected',
  action_required: 'status.actionRequired',
  error: 'status.error',
};

const STATUS_CLASS: Record<IntegrationSummary['status'], string> = {
  disconnected: 'bg-surface-2 text-muted',
  pending_setup: 'bg-warning/15 text-warning',
  connected: 'bg-success/15 text-success',
  action_required: 'bg-warning/15 text-warning',
  error: 'bg-danger/15 text-danger',
};

export function IntegrationCard({
  integration,
  workspaceId,
  comingSoon = false,
}: {
  integration: IntegrationSummary;
  workspaceId: string | null;
  comingSoon?: boolean;
}) {
  const { t } = useTranslation('integrations');
  const connect = useConnectIntegration(workspaceId);
  const disconnect = useDisconnectIntegration(workspaceId);

  async function handleConnect() {
    try {
      const result = await connect.mutateAsync({ provider: integration.provider });
      if ('url' in result) window.location.assign(result.url);
    } catch (err) {
      toast.error(t('toast.connectFailed'), { description: apiErrorMessage(err) });
    }
  }

  async function handleDisconnect() {
    try {
      await disconnect.mutateAsync(integration.provider);
      toast.success(t('toast.disconnected'));
    } catch (err) {
      toast.error(t('toast.disconnectFailed'), { description: apiErrorMessage(err) });
    }
  }

  if (comingSoon) {
    return (
      <Card className="opacity-60">
        <CardHeader>
          <ProviderLogo provider={integration.provider} />
          <CardTitle>{integration.label}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <span className="bg-surface-2 px-2.5 py-1 text-xs font-bold text-muted">{t('comingSoon')}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <ProviderLogo provider={integration.provider} />
        <CardTitle>{integration.label}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-4">
        <span className={cn('px-2.5 py-1 text-xs font-bold', STATUS_CLASS[integration.status])}>{t(STATUS_LABEL_KEYS[integration.status])}</span>
        {integration.status === 'disconnected' ? (
          <Button loading={connect.isPending} onClick={handleConnect}>
            {t('connect')}
          </Button>
        ) : (
          <Button variant="outline" loading={disconnect.isPending} onClick={handleDisconnect}>
            {t('disconnect')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
